import { useEffect, useMemo, useState } from "react";
import { PlusCircleOutlined, MoreOutlined } from "@ant-design/icons";
import { Spin, Modal, Button, Dropdown } from "antd";
import { useNavigate } from "react-router-dom";
import TableComponent, { DataTypeProps } from "../../components/TableComponent";
import MainButtonComponent from "../../components/MainButtonComponent";
import SearchComponent from "../../components/SearchComponent";
import { createColumn } from "../../utils/tableUtils";
import { getRichTextPlainText } from "../../utils/richText";
import {
	HeaderComponent,
	Container,
	ContainerHeader,
	FiltersContainer,
	ActionButtonOverlay,
	StatusCell
} from "../../components/styled/common";
import { useVolunteersFAQContext } from "../../context/VolunteersFAQContext/VolunteersFAQContext";
import { VOLUNTEERS_FAQ_LIST_PATH, VolunteersFAQListItem } from "../../context/VolunteersFAQContext/constants";

// The answer is rich text; the table shows, sorts and truncates its visible text.
type VolunteersFAQRow = VolunteersFAQListItem & { answerText: string };

const VolunteersFAQList: React.FC = () => {
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [currentItem, setCurrentItem] = useState<VolunteersFAQRow | null>(null);
	const {
		volunteersFAQList,
		clearVolunteersFAQList,
		clearVolunteersFAQFormData,
		getVolunteersFAQList,
		removeVolunteersFAQ,
		operationStatus,
	} = useVolunteersFAQContext();

	const [searchText, setSearchText] = useState("");

	const navigate = useNavigate();

	const rows = useMemo<VolunteersFAQRow[]>(
		() => volunteersFAQList.map(item => ({ ...item, answerText: getRichTextPlainText(item.answer) })),
		[volunteersFAQList]
	);

	const tableData = useMemo(() => {
		const search = searchText.trim().toLocaleLowerCase("tr-TR");
		return search
			? rows.filter(x => (x.question ?? "").toLocaleLowerCase("tr-TR").includes(search))
			: rows;
	}, [rows, searchText]);

	const isFetching = operationStatus.type === "loading" && operationStatus.operation === "fetch";
	const isDeleting = operationStatus.type === "loading" && operationStatus.operation === "delete";

	useEffect(() => {
		clearVolunteersFAQList();
		clearVolunteersFAQFormData();
		getVolunteersFAQList();

		return () => {
			clearVolunteersFAQList();
		};
	}, [clearVolunteersFAQList, clearVolunteersFAQFormData, getVolunteersFAQList]);

	// The order, status and action columns keep the width their headers need; the question and the answer share the rest.
	const columns = useMemo(() => [
		createColumn({
			title: "Sıra Numarası",
			dataIndex: "displayOrder",
			width: 150,
			sorterType: "numeric",
		}),
		createColumn({
			title: "Soru",
			dataIndex: "question",
			sorterType: "string",
		}),
		createColumn({
			title: "Cevap",
			dataIndex: "answerText",
			sorterType: "string",
			showFullTextOnHover: false,
		}),
		createColumn({
			title: "Aktiflik Durumu",
			dataIndex: "isActive",
			width: 160,
			sorterType: "boolean",
			render: (text, record) => (
				<StatusCell $bool={record.isActive}>{record.isActive ? "Aktif" : "Pasif"}</StatusCell>
			),
		}),
		createColumn({
			title: "",
			dataIndex: "action",
			width: 50,
			render: (text, record) => {
				const item = record as VolunteersFAQRow;
				const menuItems = [
					{
						key: "edit",
						label: "Güncelle",
						onClick: () => navigate(`${VOLUNTEERS_FAQ_LIST_PATH}/edit/${item.faqId}`),
					},
					{
						key: "delete",
						label: "Sil",
						onClick: () => {
							setCurrentItem(item);
							setIsModalVisible(true);
						},
					},
				];

				return (
					<Dropdown menu={{ items: menuItems }} trigger={["click"]}>
						<MoreOutlined />
					</Dropdown>
				);
			},
		}),
	], [navigate]);

	return (
		<div>
			<HeaderComponent>Merak Edilenler</HeaderComponent>
			<Container>
				<Spin spinning={isFetching}>
					<ContainerHeader style={{ justifyContent: "space-between" }}>
						<FiltersContainer>
							<SearchComponent
								placeholder="Soru Ara"
								style={{ width: "16vw" }}
								value={searchText}
								onChange={setSearchText}
							/>
						</FiltersContainer>
						<ActionButtonOverlay>
							<MainButtonComponent
								style={{ marginRight: "15px" }}
								icon={<PlusCircleOutlined />}
								onClick={() => navigate(`${VOLUNTEERS_FAQ_LIST_PATH}/add`)}
							>
								Yeni Ekle
							</MainButtonComponent>
						</ActionButtonOverlay>
					</ContainerHeader>
				</Spin>
				{!isFetching && (
					<TableComponent
						columns={columns}
						dataSource={tableData as unknown as DataTypeProps[]}
						rowKey="faqId"
					/>
				)}
			</Container>
			<Modal
				title="Merak Edilen Soruyu Sil"
				open={isModalVisible}
				onCancel={() => setIsModalVisible(false)}
				centered
				footer={[
					<Button key="back" type="text" onClick={() => setIsModalVisible(false)}>
						İptal
					</Button>,
					<Button
						loading={isDeleting}
						key="submit"
						type="primary"
						danger
						onClick={async () => {
							if (currentItem) {
								await removeVolunteersFAQ(currentItem.faqId);
							}
							setIsModalVisible(false);
						}}
					>
						Evet
					</Button>,
				]}
			>
				<p><strong>{`"${currentItem?.question ?? ""}"`}</strong> sorusunu silmek istediğinizden emin misiniz?</p>
			</Modal>
		</div>
	);
};

export default VolunteersFAQList;
